import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { applyDiscount, finalizeDiscountCodes, getCart, removeDiscount } from '../Thunks/CartThunks.jsx';
import { spreadState } from '../Helpers/StateHelper.js';
import {
  getShippingMethods,
  getExternalShippingMethods,
  setAddresses,
  setBillingAddress,
  setShippingAddress,
  setContact,
  setShippingMethod,
  setExternalShippingMethod,
} from '../Thunks/ShippingThunks.jsx';
import { handlePfasProducts } from '../Helpers/GeneralHelper.js';
import { changeProductAmount, removeProduct } from '/Foundation/Common/code/Helpers/ProductQuantityHelper';

const initialState = {
  discountAmount: 0,
  subtotal: '-',
  taxTotal: '-',
  shippingInfo: {},
  total: '-',
  cartLoaded: false,
  disableQuantityButtons: false,
  hasProductWithNoPrice: false,
  canCheckout: false,
  discountCode: '',
  lineItems: [],
  currencyCode: '',
  country: '',
  proUserDiscountLimitExceeded: false,
  unavailableProducts: [],
};

export const CartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    storeCart: (state, { payload }) => {
      state.cartLoaded = true;
      state.discountAmount = payload.discountAmount;
      state.subtotal = payload.subtotal;
      state.taxTotal = payload.taxTotal;
      state.shippingInfo = payload.shippingInfo;
      state.total = payload.totalPrice;
      state.canCheckout = payload.canCheckout;
      state.discountCode = payload.discountCode;
      state.currencyCode = payload.currencyCode;
      state.country = payload.country;
      state.disableQuantityButtons = false;
      state.proUserDiscountLimitExceeded = payload.proUserDiscountLimitExceeded;

      spreadState(state, payload);
    },

    changeProductQty: (state, { payload }) => {
      const { productId, quantity } = payload;
      let deepCopy = JSON.parse(JSON.stringify(state.lineItems));
      const product = deepCopy.find((x) => x.id === productId);
      product.quantity = quantity;

      state.lineItems = deepCopy;
    },

    toggleQtyButtons: (state) => {
      state.disableQuantityButtons = !state.disableQuantityButtons;
    },

    setUnavailableProducts: (state, { payload }) => {
      state.unavailableProducts = payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(
          getCart.fulfilled,
          changeProductAmount.fulfilled,
          removeProduct.fulfilled,
          applyDiscount.fulfilled,
          removeDiscount.fulfilled,
          finalizeDiscountCodes.fulfilled,
          setAddresses.fulfilled,
          getShippingMethods.fulfilled,
          getExternalShippingMethods.fulfilled,
          setShippingAddress.fulfilled,
          setContact.fulfilled,
          handlePfasProducts.fulfilled,
          setShippingMethod.fulfilled,
          setExternalShippingMethod.fulfilled,
          setBillingAddress.fulfilled
        ),
        (state, { payload }) => {
          // Payload data depends on the combination of shipment numbers, types of shipping methods, and where the thunk is triggered from
          const data =
            payload?.cart ||
            payload?.result?.cart ||
            payload?.nonStoreOptions?.cart ||
            payload?.storeOptions?.cart ||
            payload;

          const {
            discountAmount,
            subtotal,
            taxTotal,
            shippingInfo,
            totalPrice,
            canCheckout,
            discountCode,
            lineItems,
            currencyCode,
            country,
            proUserDiscountLimitExceeded,
            shippingAddress,
          } = data;

          let hasProductWithNoPrice = false;
          if (lineItems?.filter((x) => x.price == 0)?.length > 0) {
            hasProductWithNoPrice = true;
          }
          let action = {
            payload: {
              lineItems,
              hasProductWithNoPrice,
              discountAmount,
              subtotal,
              taxTotal,
              shippingInfo,
              totalPrice,
              canCheckout,
              discountCode,
              currencyCode,
              country,
              proUserDiscountLimitExceeded,
              hasShippingAddressSet: !!shippingAddress,
            },
          };

          CartSlice.caseReducers.storeCart(state, action);
        }
      )
      .addMatcher(isAnyOf(getCart.rejected, changeProductAmount.rejected, removeProduct.rejected), (state) => {
        state.cartLoaded = true;
      });
  },
});

export const { changeProductQty, storeCart, toggleQtyButtons, toggleExternalCheckoutMessage, setUnavailableProducts } =
  CartSlice.actions;

export default CartSlice.reducer;
